import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  NewReleases as NewReleasesIcon,
  BugReport as BugReportIcon,
  Build as BuildIcon,
  Star as StarIcon
} from '@mui/icons-material';

/**
 * Shared changelog rendering helpers.
 *
 * The changelog page and the changelog modal share the same data shape and
 * category taxonomy. This module centralises the category color/icon maps and
 * the change-rendering logic. Two presentational variants are supported so each
 * caller keeps its existing look:
 *   - variant="page":  bullet glyphs, category icons, softer indentation
 *   - variant="modal": no bullets on titles, no category icons
 */

export const getCategoryColor = (category) => {
  const colors = {
    'Hinzugefügt': 'success',
    'Geändert': 'primary',
    'Behoben': 'warning',
    'Entfernt': 'error',
    'Veraltet': 'default',
    'Sicherheit': 'error',
    'Technisch': 'info'
  };
  return colors[category] || 'default';
};

export const getCategoryIcon = (category) => {
  const icons = {
    'Hinzugefügt': <StarIcon />,
    'Behoben': <BugReportIcon />,
    'Technisch': <BuildIcon />
  };
  return icons[category] || <NewReleasesIcon />;
};

const renderChange = (change, variant) => {
  const isPage = variant === 'page';

  if (typeof change === 'string') {
    return (
      <Typography
        variant="body2"
        sx={isPage ? { mb: 0.5, color: 'text.secondary' } : { mb: 0.5 }}
      >
        {isPage ? `• ${change}` : change}
      </Typography>
    );
  }

  // Change is an object with title, description, and items
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {isPage ? '• ' : ''}{change.title}:{' '}
        <span style={{ fontWeight: 400, ...(isPage ? { color: '#666' } : {}) }}>
          {change.description}
        </span>
      </Typography>
      {change.items && change.items.length > 0 && (
        <Box sx={{ pl: isPage ? 3 : 2 }}>
          {change.items.map((item, idx) => (
            <Typography
              key={idx}
              variant="body2"
              sx={{ mb: 0.3, color: 'text.secondary', fontSize: isPage ? '0.85rem' : '0.875rem' }}
            >
              {isPage ? '◦' : '•'} {item}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * Render a version's grouped changes.
 * @param {object} props
 * @param {object} props.changes - Map of category -> array of changes
 * @param {'page'|'modal'} props.variant - Presentation variant
 */
const ChangelogChanges = ({ changes, variant = 'modal' }) => {
  const isPage = variant === 'page';

  return (
    <>
      {Object.entries(changes).map(([category, items]) => {
        if (!items || items.length === 0) return null;

        return (
          <Box key={category} sx={{ mb: isPage ? 2.5 : 2 }}>
            {isPage ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {getCategoryIcon(category)}
                <Chip
                  label={category}
                  color={getCategoryColor(category)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            ) : (
              <Chip
                label={category}
                color={getCategoryColor(category)}
                size="small"
                sx={{ mb: 1, fontWeight: 600 }}
              />
            )}
            <Box sx={{ pl: 1 }}>
              {items.map((change, idx) => (
                <Box key={idx}>{renderChange(change, variant)}</Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default ChangelogChanges;
